"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  Wallet,
  Info,
  AlertTriangle,
  Calculator,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { RelatedLinks } from "@/components/seo/RelatedLinks";
import {
  StructuredData,
  generateHowToSchema,
} from "@/components/seo/StructuredData";
import {
  BRUT_ASGARI_UCRET,
  KIDEM_TAZMINATI_TAVANI,
  YILI,
  hesaplaKidem,
  formatTL,
  type KidemSonuc,
} from "./kidem-engine";

const howToSchema = generateHowToSchema({
  title: "Kıdem Tazminatı Nasıl Hesaplanır?",
  description:
    "Brüt maaşınız ve çalışma sürenize göre kıdem tazminatınızı hesaplayın.",
  totalTime: "PT2M",
  steps: [
    {
      name: "Brüt Maaşı Girin",
      text: "Son giydirilmiş brüt maaşınızı TL olarak girin.",
    },
    {
      name: "Çalışma Süresini Belirleyin",
      text: "Yıl, ay ve gün olarak çalışma sürenizi girin.",
    },
    {
      name: "Sonuçları İnceleyin",
      text: "Brüt/net kıdem tazminatınızı, tavan kontrolünü ve damga vergisini görüntüleyin.",
    },
  ],
});

function SSSSoru({ soru, cevap }: { soru: string; cevap: string }) {
  const [acik, setAcik] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setAcik((p) => !p)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={acik}
      >
        <span className="font-semibold text-gray-900 pr-4">{soru}</span>
        {acik ? (
          <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        )}
      </button>
      {acik && (
        <div className="px-5 pb-5">
          <p className="text-gray-600 leading-relaxed">{cevap}</p>
        </div>
      )}
    </div>
  );
}

export default function KidemTazminatiHesaplayiciClient() {
  const [brutMaas, setBrutMaas] = useState("");
  const [calismaYili, setCalismaYili] = useState("");
  const [calismaAyi, setCalismaAyi] = useState("");
  const [calismaGunu, setCalismaGunu] = useState("");
  const [sonuc, setSonuc] = useState<KidemSonuc | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  const hesapla = useCallback(() => {
    setHata(null);
    const maas = parseFloat(brutMaas);
    const yil = parseInt(calismaYili) || 0;
    const ay = parseInt(calismaAyi) || 0;
    const gun = parseInt(calismaGunu) || 0;

    if (!brutMaas || isNaN(maas) || maas <= 0) {
      setHata("Lütfen geçerli bir brüt maaş giriniz.");
      setSonuc(null);
      return;
    }
    if (yil === 0 && ay === 0 && gun === 0) {
      setHata("Lütfen çalışma süresini giriniz.");
      setSonuc(null);
      return;
    }
    if (yil * 12 + ay < 12 && gun < 365) {
      setHata(
        "Kıdem tazminatı hakkı için en az 1 yıl çalışmış olmanız gerekir."
      );
      setSonuc(null);
      return;
    }

    const hesapSonuc = hesaplaKidem({
      brutMaas: maas,
      calismaYili: yil,
      calismaAyi: ay,
      calismaGunu: gun,
    });
    setSonuc(hesapSonuc);
  }, [brutMaas, calismaYili, calismaAyi, calismaGunu]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        hesapla();
      }
    },
    [hesapla]
  );

  return (
    <>
      <StructuredData data={[howToSchema]} />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Breadcrumbs
              items={[
                { label: "Araçlar", href: "/araclar" },
                {
                  label: "Kıdem Tazminatı Hesaplayıcı",
                  href: "/araclar/kidem-tazminati-hesaplayici",
                },
              ]}
              className="mb-8 text-white/70"
            />
            <Link
              href="/araclar"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Tüm Araçlar
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                <Wallet className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold">
                  Kıdem Tazminatı Hesaplayıcı
                </h1>
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm font-medium backdrop-blur-sm">
                  {YILI} Tavan: {formatTL(KIDEM_TAZMINATI_TAVANI)}
                </span>
              </div>
            </div>
            <p className="text-lg text-white/90 max-w-2xl">
              Çalışma sürenize ve brüt maaşınıza göre kıdem tazminatınızı
              hesaplayın. {YILI} güncel tavan tutarı ve damga vergisi dahil.
            </p>
          </div>
        </section>

        {/* Hesaplama */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              {/* Tavan Bilgisi */}
              <div className="mb-8 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <h2 className="text-sm font-semibold text-gray-900 mb-2">
                  {YILI} Kıdem Tazminatı Tavanı (Ocak–Haziran)
                </h2>
                <span className="text-2xl font-bold text-emerald-600">
                  {formatTL(KIDEM_TAZMINATI_TAVANI)}
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  Brüt maaş bu tutarı aşarsa, hesaplama tavan üzerinden yapılır.
                </p>
              </div>

              {/* Girdiler */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div className="sm:col-span-2">
                  <label
                    htmlFor="brutMaas"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Giydirilmiş Brüt Maaş
                  </label>
                  <div className="relative">
                    <input
                      id="brutMaas"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      value={brutMaas}
                      onChange={(e) => setBrutMaas(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="0,00"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-gray-900"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                      ₺
                    </span>
                  </div>
                  <div className="flex gap-4 mt-2">
                    <button
                      type="button"
                      onClick={() => setBrutMaas(BRUT_ASGARI_UCRET.toString())}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                    >
                      {YILI} Asgari Ücret ({formatTL(BRUT_ASGARI_UCRET)})
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Yemek, yol, prim dahil giydirilmiş brüt kullanılır
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="calismaYili"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Çalışma Yılı
                  </label>
                  <input
                    id="calismaYili"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    max="50"
                    value={calismaYili}
                    onChange={(e) => setCalismaYili(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-gray-900"
                  />
                </div>

                <div>
                  <label
                    htmlFor="calismaAyi"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Ay
                  </label>
                  <input
                    id="calismaAyi"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    max="11"
                    value={calismaAyi}
                    onChange={(e) => setCalismaAyi(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-gray-900"
                  />
                </div>

                <div>
                  <label
                    htmlFor="calismaGunu"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Gün
                  </label>
                  <input
                    id="calismaGunu"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    max="29"
                    value={calismaGunu}
                    onChange={(e) => setCalismaGunu(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-gray-900"
                  />
                </div>
              </div>

              {hata && (
                <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{hata}</p>
                </div>
              )}

              <button
                type="button"
                onClick={hesapla}
                className="w-full bg-emerald-600 text-white py-4 rounded-xl font-semibold hover:bg-emerald-700 active:bg-emerald-800 transition-colors shadow-sm"
              >
                <span className="flex items-center justify-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Hesapla
                </span>
              </button>
            </div>

            {/* Sonuç */}
            {sonuc && (
              <div className="border-t border-gray-200 bg-gray-50 p-6 sm:p-8">
                {sonuc.tavanUygulandi && (
                  <div className="mb-4 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-700">
                      <strong>Tavan Uyarısı:</strong> Maaşınız {YILI} kıdem
                      tavanı olan {formatTL(KIDEM_TAZMINATI_TAVANI)}&apos;yi
                      aştığı için hesaplama tavan üzerinden yapılmıştır.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-emerald-600 text-white rounded-xl p-4">
                    <span className="text-xs text-emerald-100 block">
                      Çalışma Süresi
                    </span>
                    <span className="text-xl font-bold mt-1 block">
                      {Math.floor(sonuc.toplamYil)} yıl {sonuc.kalanAy} ay
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 block">
                      Brüt Tazminat
                    </span>
                    <span className="text-lg font-bold text-gray-900 mt-1 block tabular-nums">
                      {formatTL(sonuc.brutKidemTazminati)}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 block">
                      Damga Vergisi
                    </span>
                    <span className="text-lg font-bold text-red-500 mt-1 block tabular-nums">
                      -{formatTL(sonuc.damgaVergisi)}
                    </span>
                    <span className="text-xs text-gray-400">%0,759</span>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                    <span className="text-xs text-emerald-600 block">
                      Net Tazminat
                    </span>
                    <span className="text-lg font-bold text-emerald-700 mt-1 block tabular-nums">
                      {formatTL(sonuc.netKidemTazminati)}
                    </span>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-5 border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-3">
                    Hesaplama Detayı
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Hesaplanadaki brüt maaş
                      </span>
                      <span className="font-medium tabular-nums">
                        {formatTL(sonuc.hesaplamaBrutMaas)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Toplam çalışma (yıl)
                      </span>
                      <span className="font-medium tabular-nums">
                        {sonuc.toplamYil.toFixed(4)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-gray-600">
                        Formül: {sonuc.toplamYil.toFixed(2)} ×{" "}
                        {formatTL(sonuc.hesaplamaBrutMaas)}
                      </span>
                      <span className="font-bold text-emerald-600 tabular-nums">
                        {formatTL(sonuc.brutKidemTazminati)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* SSS */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Kıdem Tazminatı Rehberi ({YILI})
          </h2>
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-sm space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Kıdem Tazminatına Kim Hak Kazanır?
              </h3>
              <ul className="text-gray-700 space-y-2 text-sm">
                <li>• En az 1 yıl aynı işverende çalışmış olmak</li>
                <li>• Geçerli neden olmadan işten çıkarılmak</li>
                <li>
                  • Kadın çalışanların evlilik nedeniyle 1 yıl içinde istifası
                </li>
                <li>• Askerlik görevi, emeklilik veya ölüm</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Sık Sorulan Sorular
              </h3>
              <div className="space-y-3">
                <SSSSoru
                  soru="Kıdem tazminatı vergiye tabi midir?"
                  cevap="Kıdem tazminatı gelir vergisinden muaftır. Sadece binde 7,59 oranında damga vergisine tabidir."
                />
                <SSSSoru
                  soru="Giydirilmiş brüt maaş nedir?"
                  cevap="Giydirilmiş brüt maaş, çıplak brüt maaşın üzerine yemek, yol, ikramiye gibi düzenli ödemelerin eklenmesiyle oluşan toplam brüt tutardır. Kıdem tazminatı hesaplamasında bu tutar kullanılır."
                />
                <SSSSoru
                  soru="Kıdem tazminatı tavanı ne işe yarar?"
                  cevap={`${YILI} yılı için kıdem tazminatı tavanı ${formatTL(KIDEM_TAZMINATI_TAVANI)}'dir. Brüt maaşınız bu tutarı aşsa bile, hesaplama tavan üzerinden yapılır.`}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="p-5 bg-amber-50 rounded-xl border border-amber-100">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-gray-600">
                {YILI} Ocak–Haziran dönemi tavanı kullanılmıştır. Kesin
                hesaplama için iş hukuku danışmanınıza başvurunuz.
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-8">
          <RelatedLinks
            pageType="tool"
            currentSlug="kidem-tazminati-hesaplayici"
            title="İlgili Sayfalar"
            maxLinks={4}
          />
        </section>

        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-emerald-600 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">
              İK Süreçlerinizi Otomatikleştirin
            </h2>
            <p className="text-emerald-100 mb-6 max-w-xl mx-auto">
              IKAI ile kıdem, ihbar, izin hesaplamaları ve tüm İK süreçlerinizi
              tek platformda yönetin.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 bg-white text-emerald-700 px-6 py-3 rounded-xl font-semibold hover:bg-emerald-50 transition-colors"
            >
              Ücretsiz Başlayın
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
